import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ClockIcon, CheckCircle2Icon, MailIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { LogoMark } from '../components/LogoMark';
import { FEATURED_INSIGHT, INSIGHTS, INSIGHT_FILTERS } from '../data/site';
export function Insights() {
  const [filter, setFilter] = useState('All');
  const [subscribed, setSubscribed] = useState(false);
  const filtered = INSIGHTS.filter(a => filter === 'All' || a.category === filter);
  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    // eslint-disable-next-line no-console
    console.log('field_notes_signup', {
      source: 'insights_page'
    });
    setSubscribed(true);
  };
  return <>
      <section className="relative w-full overflow-hidden bg-cream py-16 lg:py-24" data-id="element-361">
        <div className="mgs-grid absolute inset-0" aria-hidden data-id="element-362" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8" data-id="element-363">
          <div className="max-w-3xl" data-id="element-364">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-warmgray" data-id="element-365">
              <LogoMark className="h-3.5 w-3.5" data-id="element-366" /> Insights
            </p>
            <h1 className="mt-3 text-4xl font-black leading-[1.05] tracking-tight text-charcoal sm:text-5xl" data-id="element-367">
              Mine Geology Systems Insights
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-warmgray" data-id="element-368">
              Case studies, framework articles and practical field notes — real
              problems, practical systems thinking, no invented client results.
              The first articles are being written now.
            </p>
          </div>
        </div>
      </section>

      <section className="w-full bg-sand py-20 lg:py-28" data-id="element-369">
        <div className="mx-auto max-w-7xl px-5 lg:px-8" data-id="element-370">
          <motion.article initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45
        }} className="overflow-hidden rounded-2xl border border-charcoal/10 bg-charcoal shadow-card" data-id="element-371">
            <div className="h-1.5 w-full" style={{
            backgroundColor: FEATURED_INSIGHT.categoryColor
          }} aria-hidden data-id="element-372" />
            <div className="grid gap-8 p-7 lg:grid-cols-12 lg:items-center lg:p-12" data-id="element-373">
              <div className="lg:col-span-8" data-id="element-374">
                <div className="flex items-center gap-3" data-id="element-375">
                  <span className="rounded-full bg-cream/10 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-cream" data-id="element-376">
                    Featured · {FEATURED_INSIGHT.category}
                  </span>
                  <span className="rounded-full bg-yellow px-2.5 py-1 text-xs font-bold text-charcoal" data-id="element-377">
                    {FEATURED_INSIGHT.status}
                  </span>
                </div>
                <h2 className="mt-4 text-2xl font-black leading-tight text-cream sm:text-3xl" data-id="element-378">
                  {FEATURED_INSIGHT.title}
                </h2>
                <p className="mt-3 max-w-2xl text-base leading-relaxed text-cream/80" data-id="element-379">
                  {FEATURED_INSIGHT.summary}
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right" data-id="element-380">
                <p className="inline-flex items-center gap-1.5 text-xs text-cream/60" data-id="element-381">
                  <ClockIcon className="h-3.5 w-3.5" data-id="element-382" />
                  {FEATURED_INSIGHT.readTime}
                </p>
                <div className="mt-3" data-id="element-383">
                  <span className="inline-flex items-center gap-2 rounded-md border border-cream/25 px-5 py-3 text-sm font-bold text-cream" data-id="element-384">
                    Read insight
                    <ArrowRightIcon className="h-4 w-4 text-yellow" data-id="element-385" />
                  </span>
                </div>
              </div>
            </div>
          </motion.article>

          <div className="mt-14 flex flex-wrap gap-2" role="group" aria-label="Filter insights" data-id="element-386">
            {INSIGHT_FILTERS.map(f => <button key={f} type="button" onClick={() => setFilter(f)} aria-pressed={filter === f} className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${filter === f ? 'border-charcoal bg-charcoal text-cream' : 'border-charcoal/15 bg-cream text-charcoal/75 hover:border-charcoal/40'}`} data-id="element-387">
                {f}
              </button>)}
          </div>

          <div className="mt-10 grid items-stretch gap-6 md:grid-cols-2" data-id="element-388">
            {filtered.map((a, i) => <motion.article key={a.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 0.35,
            delay: i * 0.04
          }} className="group flex h-full flex-col rounded-xl border border-charcoal/10 bg-cream p-6 shadow-card transition-transform duration-200 hover:-translate-y-1" data-id="element-389">
                <div className="flex items-center gap-3" data-id="element-390">
                  <span className="rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-white" style={{
                backgroundColor: a.categoryColor
              }} data-id="element-391">
                    {a.category}
                  </span>
                  <span className="rounded-full bg-charcoal/8 px-2.5 py-1 text-xs font-bold text-warmgray" data-id="element-392">
                    {a.status}
                  </span>
                </div>
                <h2 className="mt-4 text-xl font-bold leading-snug text-charcoal" data-id="element-393">
                  {a.title}
                </h2>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-warmgray" data-id="element-394">
                  {a.summary}
                </p>
                <div className="mt-auto flex items-center justify-between border-t border-charcoal/10 pt-4" data-id="element-395">
                  <span className="inline-flex items-center gap-1.5 text-xs text-warmgray" data-id="element-396">
                    <ClockIcon className="h-3.5 w-3.5" data-id="element-397" />
                    {a.readTime}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-sm font-bold text-charcoal" data-id="element-398">
                    Read insight
                    <ArrowRightIcon className="h-4 w-4 text-yellow transition-transform group-hover:translate-x-0.5" data-id="element-399" />
                  </span>
                </div>
              </motion.article>)}
          </div>
          {filtered.length === 0 && <p className="mt-10 text-center text-sm text-warmgray" data-id="element-400">
              No insights in this category yet.
            </p>}
        </div>
      </section>

      <section className="w-full bg-charcoal py-20 lg:py-24" data-id="element-401">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8" data-id="element-402">
          <LogoMark className="mx-auto h-7 w-7" title="Mine Geology Systems" data-id="element-403" />
          <h2 className="mt-5 text-3xl font-black leading-tight text-cream sm:text-4xl" data-id="element-404">
            Get future MGS insights in your inbox.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-cream/80" data-id="element-405">
            Field Notes is occasional and practical — new articles, case studies
            and resources, sent only when there's something worth reading.
          </p>
          {subscribed ? <div className="mx-auto mt-8 inline-flex items-center gap-2 rounded-md bg-improvements/20 px-5 py-3 text-sm font-medium text-cream" data-id="element-406">
              <CheckCircle2Icon className="h-5 w-5 text-improvements" data-id="element-407" />
              You're on the list. Welcome to Field Notes.
            </div> : <form onSubmit={handleSubscribe} className="mx-auto mt-8 flex max-w-md flex-col gap-3 sm:flex-row" data-id="element-408">
              <label htmlFor="ins-email" className="sr-only" data-id="element-409">
                Email address
              </label>
              <div className="relative flex-1" data-id="element-410">
                <MailIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-warmgray" data-id="element-411" />
                <input id="ins-email" type="email" required placeholder="you@example.com" className="w-full rounded-md border border-cream/20 bg-cream py-3 pl-9 pr-3 text-sm text-charcoal placeholder:text-warmgray/60" data-id="element-412" />
              </div>
              <button type="submit" className="rounded-md bg-yellow px-5 py-3 text-sm font-bold text-charcoal hover:brightness-95" data-id="element-413">
                Join Field Notes
              </button>
            </form>}
          <p className="mt-6 text-xs text-cream/60" data-id="element-414">
            Want the foundations first?{' '}
            <Button to="/framework" variant="ghost" className="px-2 py-1 text-xs text-yellow hover:bg-cream/10" data-id="element-415">
              Explore the Framework →
            </Button>
          </p>
        </div>
      </section>
    </>;
}